import React, { useCallback } from "react";
import PropTypes from "prop-types";
import Dropzone from "react-dropzone";
import {
  TvButton,
  TvDiv,
  TvFormGroup,
  TvI,
  TvIconButton,
  TvInput,
  TvLabel,
  TvP,
  TvSmall,
} from "@trustvid/design-system";
import {
  getSmartCampaignComposerHint,
  getSmartCampaignComposerLabel,
  matchSmartCampaignSamplePromptId,
  SMART_CAMPAIGN_PROMPT_PLACEHOLDERS,
  SMART_CAMPAIGN_REQUIREMENT_HINT,
} from "../constants/smartCampaignPromptGuide";
import { SMART_CAMPAIGN_MAX_UPLOAD_FILES } from "../constants/smartCampaignUploadLimits";
import AiCampaignSamplePrompts from "./AiCampaignSamplePrompts";

export const AI_CAMPAIGN_ACCEPTED_FILE_TYPES = {
  "image/*": [".jpg", ".jpeg", ".png", ".webp", ".gif"],
  "video/*": [".mp4", ".mov", ".webm"],
  "audio/*": [".mp3", ".wav", ".m4a", ".aac"],
  "application/pdf": [".pdf"],
  "text/plain": [".txt", ".text"],
};

const DEFAULT_PLACEHOLDER = SMART_CAMPAIGN_PROMPT_PLACEHOLDERS.empty;

function mergeFiles(existingFiles, incomingFiles, maxFiles) {
  const merged = [...existingFiles];
  for (const file of incomingFiles) {
    if (merged.length >= maxFiles) {
      break;
    }
    merged.push(file);
  }
  return merged;
}

/**
 * Theme tokens aligned with form-control / editor-theme.scss so the composer
 * matches branded inputs, buttons, and surfaces elsewhere in Create Design.
 */
function getComposerTheme(isDragActive) {
  return {
    shell: {
      backgroundColor: "var(--tv-theme-surface, var(--bs-body-bg, #ffffff))",
      border: isDragActive
        ? "2px solid var(--tv-theme-primary, var(--bs-primary))"
        : "1px solid var(--tv-theme-border, var(--bs-border-color, #dee2e6))",
      borderRadius: "var(--tv-radius-sm, 0.375rem)",
      color: "var(--tv-theme-text, var(--bs-body-color))",
    },
    dragOverlay: {
      backgroundColor: "color-mix(in srgb, var(--tv-theme-primary, var(--bs-primary)) 10%, transparent)",
      color: "var(--tv-theme-primary, var(--bs-primary))",
    },
    textarea: {
      backgroundColor: "transparent",
      color: "var(--tv-theme-text, var(--bs-body-color))",
      border: "none",
      boxShadow: "none",
    },
    fileChip: {
      backgroundColor: "var(--tv-theme-surface-muted, var(--bs-secondary-bg, #f8f9fa))",
      border: "1px solid var(--tv-theme-border, var(--bs-border-color, #dee2e6))",
      color: "var(--tv-theme-text, var(--bs-body-color))",
    },
    toolbar: {
      borderTop: "1px solid var(--tv-theme-border, var(--bs-border-color, #dee2e6))",
    },
  };
}

const AiCampaignPromptComposer = ({
  prompt = "",
  onPromptChange = () => {},
  files = [],
  onFilesChange = () => {},
  onSubmit = () => {},
  isSubmitting = false,
  canSubmit = false,
  maxFiles = SMART_CAMPAIGN_MAX_UPLOAD_FILES,
  placeholder,
  fileError = "",
  onFileError = () => {},
  readyMessage = SMART_CAMPAIGN_REQUIREMENT_HINT,
  inputMode = "empty",
  variant = "campaign",
  hideGenerateButton = false,
}) => {
  const resolvedPlaceholder = placeholder || SMART_CAMPAIGN_PROMPT_PLACEHOLDERS[inputMode] || DEFAULT_PLACEHOLDER;

  const selectedSamplePromptId = matchSmartCampaignSamplePromptId(prompt);

  const handleAcceptedFiles = useCallback((acceptedFiles) => {
    if (!acceptedFiles.length) {
      return;
    }
    onFileError("");
    const merged = mergeFiles(files, acceptedFiles, maxFiles);
    if (merged.length > maxFiles || files.length + acceptedFiles.length > maxFiles) {
      onFileError(`You can upload up to ${maxFiles} files at a time.`);
    }
    onFilesChange(merged.slice(0, maxFiles));
  }, [files, maxFiles, onFileError, onFilesChange]);

  const handleRemoveFile = (index) => {
    onFilesChange(files.filter((_, fileIndex) => fileIndex !== index));
    onFileError("");
  };

  const handleSubmitClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (canSubmit && !isSubmitting) {
      return onSubmit();
    }
  };

  if (variant === "studio") {
    return (
      <TvFormGroup className="mb-0">
        <AiCampaignSamplePrompts
          appearance="chips"
          activeMode={inputMode}
          selectedSamplePromptId={selectedSamplePromptId}
          hasFiles={files.length > 0}
          onSelectPrompt={(sample) => onPromptChange(sample.prompt)}
          disabled={isSubmitting}
        />

        <Dropzone
          onDrop={handleAcceptedFiles}
          accept={AI_CAMPAIGN_ACCEPTED_FILE_TYPES}
          multiple
          maxFiles={maxFiles}
          noClick
          noKeyboard
          disabled={isSubmitting}
        >
          {({ getRootProps, getInputProps, isDragActive, open }) => (
            <TvDiv
              {...getRootProps({
                className: `p-3 position-relative rounded-4 border bg-white ${isDragActive ? "border-primary" : ""}`,
              })}
              data-testid="ai-campaign-prompt-composer"
            >
              <input {...getInputProps()} data-testid="ai-campaign-composer-drop-input" />

              {isDragActive ? (
                <TvDiv
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-4 bg-primary-subtle"
                  data-testid="ai-campaign-composer-drag-overlay"
                >
                  <TvSmall className="fw-semibold text-primary">Drop files to attach</TvSmall>
                </TvDiv>
              ) : null}

              <TvInput
                id="ai-campaign-prompt-composer-input"
                type="textarea"
                rows={5}
                value={prompt}
                onChange={(event) => onPromptChange(event.target.value)}
                placeholder={resolvedPlaceholder}
                data-testid="smart-campaign-prompt"
                className="w-100 mb-3 border-0 shadow-none px-1"
                disabled={isSubmitting}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              />

              {files.length > 0 ? (
                <TvDiv className="d-flex flex-wrap gap-2 mb-3" data-testid="ai-campaign-attached-files">
                  {files.map((file, index) => (
                    <TvDiv
                      key={`${file.name}-${file.size}-${index}`}
                      className="d-inline-flex align-items-center gap-2 rounded-pill border px-3 py-1 small bg-light"
                      data-testid={`ai-campaign-file-chip-${index}`}
                    >
                      <TvI className="fa fa-paperclip text-muted" aria-hidden="true" />
                      <span className="text-truncate" title={file.name}>
                        {file.name}
                      </span>
                      <TvButton
                        type="button"
                        color="link"
                        className="p-0 text-decoration-none"
                        aria-label={`Remove ${file.name}`}
                        data-testid={`ai-campaign-remove-file-${index}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleRemoveFile(index);
                        }}
                        disabled={isSubmitting}
                      >
                        <TvI className="fa fa-times" aria-hidden="true" />
                      </TvButton>
                    </TvDiv>
                  ))}
                </TvDiv>
              ) : null}

              {fileError ? (
                <TvSmall className="d-block mb-2 text-danger">
                  {fileError}
                </TvSmall>
              ) : null}

              <TvDiv className="rounded-3 border bg-light p-3 d-flex flex-wrap align-items-center gap-3">
                <TvIconButton
                  type="button"
                  color="primary"
                  aria-label="Attach files"
                  data-testid="ai-campaign-attach-files"
                  className="rounded-circle d-inline-flex align-items-center justify-content-center"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    open();
                  }}
                  disabled={isSubmitting}
                >
                  <TvI className="fa fa-cloud-upload-alt" aria-hidden="true" />
                </TvIconButton>
                <TvDiv>
                  <TvSmall className="fw-semibold d-block mb-0">Add files</TvSmall>
                  <TvSmall className="text-muted mb-0">
                    Image, video, PDF, audio or narration
                    {files.length > 0 ? ` · ${files.length}/${maxFiles}` : ""}
                  </TvSmall>
                </TvDiv>
              </TvDiv>

              <TvSmall
                className={`d-block mt-2 mb-0 ${canSubmit ? "text-success" : "text-muted"}`}
                data-testid="ai-campaign-ready-message"
              >
                {readyMessage}
              </TvSmall>

              {hideGenerateButton ? null : (
                <TvDiv className="d-flex justify-content-end mt-3">
                  <TvButton
                    type="button"
                    color="primary"
                    className="rounded-pill px-4"
                    data-testid="smart-campaign-generate-draft"
                    disabled={!canSubmit || isSubmitting}
                    onClick={handleSubmitClick}
                  >
                    {isSubmitting ? "Generating..." : "Generate template ✨"}
                  </TvButton>
                </TvDiv>
              )}
            </TvDiv>
          )}
        </Dropzone>
      </TvFormGroup>
    );
  }

  return (
    <TvFormGroup className="mb-0">
      <TvLabel for="ai-campaign-prompt-composer-input" className="fw-semibold">
        {getSmartCampaignComposerLabel(inputMode)}
      </TvLabel>
      <TvP className="text-muted small mb-2">
        {getSmartCampaignComposerHint(inputMode)}
      </TvP>

      <AiCampaignSamplePrompts
        activeMode={inputMode}
        selectedSamplePromptId={selectedSamplePromptId}
        hasFiles={files.length > 0}
        onSelectPrompt={(sample) => onPromptChange(sample.prompt)}
        disabled={isSubmitting}
      />

      <Dropzone
        onDrop={handleAcceptedFiles}
        accept={AI_CAMPAIGN_ACCEPTED_FILE_TYPES}
        multiple
        maxFiles={maxFiles}
        noClick
        noKeyboard
        disabled={isSubmitting}
      >
        {({ getRootProps, getInputProps, isDragActive, open }) => {
          const theme = getComposerTheme(isDragActive);

          return (
            <TvDiv
              {...getRootProps({
                className: "p-3 position-relative",
                style: theme.shell,
              })}
              data-testid="ai-campaign-prompt-composer"
            >
              <input {...getInputProps()} data-testid="ai-campaign-composer-drop-input" />

              {isDragActive ? (
                <TvDiv
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{
                    ...theme.dragOverlay,
                    borderRadius: "var(--tv-radius-sm, 0.375rem)",
                  }}
                  data-testid="ai-campaign-composer-drag-overlay"
                >
                  <TvSmall className="fw-semibold">Drop files to attach</TvSmall>
                </TvDiv>
              ) : null}

              <TvInput
                id="ai-campaign-prompt-composer-input"
                type="textarea"
                rows={6}
                value={prompt}
                onChange={(event) => onPromptChange(event.target.value)}
                placeholder={resolvedPlaceholder}
                data-testid="smart-campaign-prompt"
                className="w-100 mb-2 shadow-none"
                style={theme.textarea}
                disabled={isSubmitting}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              />

              {files.length > 0 ? (
                <TvDiv className="d-flex flex-wrap gap-2 mb-2" data-testid="ai-campaign-attached-files">
                  {files.map((file, index) => (
                    <TvDiv
                      key={`${file.name}-${file.size}-${index}`}
                      className="d-inline-flex align-items-center gap-2 rounded-pill px-3 py-1 small"
                      style={theme.fileChip}
                      data-testid={`ai-campaign-file-chip-${index}`}
                    >
                      <TvI className="fa fa-paperclip text-muted" aria-hidden="true" />
                      <span className="text-truncate" title={file.name}>
                        {file.name}
                      </span>
                      <TvButton
                        type="button"
                        color="link"
                        className="p-0 text-decoration-none"
                        style={{ color: "var(--tv-theme-error, var(--bs-danger))" }}
                        aria-label={`Remove ${file.name}`}
                        data-testid={`ai-campaign-remove-file-${index}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleRemoveFile(index);
                        }}
                        disabled={isSubmitting}
                      >
                        <TvI className="fa fa-times" aria-hidden="true" />
                      </TvButton>
                    </TvDiv>
                  ))}
                </TvDiv>
              ) : null}

              {fileError ? (
                <TvSmall
                  className="d-block mb-2"
                  style={{ color: "var(--tv-theme-error, var(--bs-danger))" }}
                >
                  {fileError}
                </TvSmall>
              ) : null}

              <TvDiv
                className="d-flex flex-wrap align-items-center justify-content-between gap-2 pt-2"
                style={theme.toolbar}
              >
                <TvDiv className="d-flex flex-column gap-1">
                  <TvDiv className="d-flex align-items-center gap-2">
                    <TvIconButton
                      type="button"
                      color="outline-primary"
                      aria-label="Attach files"
                      data-testid="ai-campaign-attach-files"
                      className="rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{ width: "2.25rem", height: "2.25rem" }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        open();
                      }}
                      disabled={isSubmitting}
                    >
                      <TvI className="fa fa-plus" aria-hidden="true" />
                    </TvIconButton>
                    <TvSmall className="text-muted mb-0">
                      PDF, image, video, audio, narration (.txt)
                      {files.length > 0 ? ` · ${files.length}/${maxFiles}` : ""}
                    </TvSmall>
                  </TvDiv>
                  <TvSmall
                    className={`mb-0 ${canSubmit ? "text-success" : "text-muted"}`}
                    data-testid="ai-campaign-ready-message"
                  >
                    {readyMessage}
                  </TvSmall>
                </TvDiv>

                <TvButton
                  type="button"
                  color="primary"
                  data-testid="smart-campaign-generate-draft"
                  disabled={!canSubmit || isSubmitting}
                  onClick={handleSubmitClick}
                >
                  {isSubmitting ? "Generating..." : "Generate"}
                </TvButton>
              </TvDiv>
            </TvDiv>
          );
        }}
      </Dropzone>
    </TvFormGroup>
  );
};

AiCampaignPromptComposer.propTypes = {
  prompt: PropTypes.string,
  onPromptChange: PropTypes.func,
  files: PropTypes.arrayOf(PropTypes.instanceOf(File)),
  onFilesChange: PropTypes.func,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  canSubmit: PropTypes.bool,
  maxFiles: PropTypes.number,
  placeholder: PropTypes.string,
  fileError: PropTypes.string,
  onFileError: PropTypes.func,
  readyMessage: PropTypes.string,
  inputMode: PropTypes.oneOf(["empty", "uploads-only", "prompt-only", "uploads-and-prompt"]),
  variant: PropTypes.oneOf(["campaign", "studio"]),
  hideGenerateButton: PropTypes.bool,
};

export default AiCampaignPromptComposer;
